import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, orderBy, 
  where, Timestamp, doc, updateDoc, deleteDoc 
} from 'firebase/firestore';
import Swal from 'sweetalert2';

function Creditcustomers() {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Form States
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    creditLimit: 0,
    openingBalance: 0
  });

  const userId = auth.currentUser?.uid;
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
  });

  useEffect(() => {
    if (!userId) return;

    const q = query(
      collection(db, "credit_customers"), 
      where("userId", "==", userId), 
      orderBy("name", "asc")
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [userId]);

  // Handle Input Changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Create or Update
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) {
      return Toast.fire({ icon: 'warning', title: 'Name and Mobile are required' });
    }

    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, "credit_customers", editId), {
          ...formData,
          updatedAt: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Customer Updated' });
      } else {
        await addDoc(collection(db, "credit_customers"), {
          ...formData,
          userId,
          currentBalance: parseFloat(formData.openingBalance) || 0,
          createdAt: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Customer Added' });
      }
      resetForm();
      // eslint-disable-next-line no-undef
      $('#customerModal').modal('hide'); 
    } catch (err) {
      Toast.fire({ icon: 'error', title: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (c) => {
    setEditId(c.id);
    setFormData({
      name: c.name,
      mobile: c.mobile,
      address: c.address,
      creditLimit: c.creditLimit,
      openingBalance: c.openingBalance
    });
    // eslint-disable-next-line no-undef
    $('#customerModal').modal('show');
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "Customer and their history will be affected!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      await deleteDoc(doc(db, "credit_customers", id));
      Toast.fire({ icon: 'success', title: 'Customer Deleted' });
    }
  };

  const resetForm = () => {
    setEditId(null);
    setFormData({ name: "", mobile: "", address: "", creditLimit: 0, openingBalance: 0 });
  };

  // Search Logic
  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.mobile.includes(searchTerm)
  );

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  return (
    <div className="content-wrapper">
      <section className="content-header py-3">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 shadow-sm rounded border">
            <h4 className="m-0 font-weight-bold text-navy">
              <i className="fas fa-users-cog mr-2"></i>Credit Customers (Khata)
            </h4>
            <button className="btn btn-primary shadow-sm" data-toggle="modal" data-target="#customerModal" onClick={resetForm}>
              <i className="fas fa-plus mr-2"></i>Add New Customer
            </button>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white py-3">
              <div className="row align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text bg-light border-right-0"><i className="fas fa-search text-muted"></i></span>
                    </div>
                    <input type="text" className="form-control border-left-0" placeholder="Search by name or mobile..." 
                      value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
                <div className="col-md-8 text-right text-muted small font-weight-bold uppercase">
                  Total Customers: {filteredCustomers.length}
                </div>
              </div>
            </div>

            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover align-middle m-0">
                  <thead className="bg-light text-xs text-uppercase">
                    <tr>
                      <th className="text-center" style={{width: 60}}>Sr.</th>
                      <th>Customer Name</th>
                      <th>Contact Info</th>
                      <th>Credit Limit</th>
                      <th>Current Balance</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((c, index) => (
                      <tr key={c.id}>
                        <td className="text-center text-muted font-weight-bold">{indexOfFirstItem + index + 1}</td>
                        <td>
                          <div className="font-weight-bold text-navy">{c.name}</div>
                          <small className="text-muted">{c.address || 'No Address'}</small>
                        </td>
                        <td>
                          <i className="fas fa-phone-alt mr-2 text-xs text-success"></i>{c.mobile}
                        </td>
                        <td>₹ {parseFloat(c.creditLimit).toLocaleString()}</td>
                        <td className="font-weight-bold text-danger">
                          ₹ {parseFloat(c.currentBalance || 0).toLocaleString()}
                        </td>
                        <td className="text-center">
                          <button className="btn btn-sm btn-outline-primary mr-2" onClick={() => handleEdit(c)}>
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(c.id)}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Footer */}
            {totalPages > 1 && (
              <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center">
                <div className="small text-muted">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCustomers.length)} of {filteredCustomers.length}
                </div>
                <ul className="pagination pagination-sm m-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(prev => prev - 1)}>Previous</button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(prev => prev + 1)}>Next</button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CUSTOMER MODAL */}
      <div className="modal fade" id="customerModal" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content border-0 shadow-lg">
            <form onSubmit={handleSubmit}>
              <div className={`modal-header ${editId ? 'bg-warning' : 'bg-primary'} text-white`}>
                <h5 className="modal-title font-weight-bold">{editId ? 'Edit Customer' : 'New Credit Customer'}</h5>
                <button type="button" className="close text-white" data-dismiss="modal"><span>&times;</span></button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="small font-weight-bold">Full Name *</label>
                  <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Mobile Number *</label>
                  <input type="text" className="form-control" name="mobile" value={formData.mobile} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label className="small font-weight-bold">Address</label>
                  <textarea className="form-control" name="address" rows="2" value={formData.address} onChange={handleChange}></textarea>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="form-group">
                      <label className="small font-weight-bold">Credit Limit (₹)</label>
                      <input type="number" className="form-control" name="creditLimit" value={formData.creditLimit} onChange={handleChange} />
                    </div>
                  </div>
                 
                </div>
              </div>
              <div className="modal-footer bg-light border-0">
                <button type="button" className="btn btn-secondary px-4" data-dismiss="modal">Cancel</button>
                <button type="submit" disabled={loading} className={`btn px-4 font-weight-bold ${editId ? 'btn-warning' : 'btn-primary'}`}>
                  {loading ? 'Processing...' : editId ? 'Save Changes' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Creditcustomers;